import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["panel"];
  static values = {
    preventClose: {
      type: Boolean,
      default: false,
    },
  };

  connect() {
    setTimeout(() => {
      this.element.classList.remove("opacity-0");
      this.panelTarget.classList.remove("translate-x-full");
    }, 50);

    // An AbortController rather than a bound reference kept on
    // the instance. `removeEventListener` matches a listener by
    // identity and `.bind` mints a new function on every call,
    // so the symmetrical looking pair this replaces removed
    // nothing: every slideout opened left a keydown listener on
    // `document` for the rest of the page's life, each one
    // re-entering `close()` on an overlay that had gone. A
    // signal is the platform's own answer and cannot drift out
    // of step the way a stored reference can, and it covers
    // whatever listener is added here next for free.
    this.listeners = new AbortController();

    document.addEventListener(
      "keydown",
      this.closeWithEscapeKey.bind(this),
      { signal: this.listeners.signal }
    );
  }

  disconnect() {
    this.listeners.abort();
  }

  closeWithEscapeKey(event) {
    if (event.key === "Escape") {
      // Don't close if focused on an input field (for autocomplete, etc.)
      const activeElement = document.activeElement;
      const isInputField = activeElement && (
        activeElement.tagName === "INPUT" ||
        activeElement.tagName === "TEXTAREA" ||
        activeElement.tagName === "SELECT" ||
        activeElement.contentEditable === "true"
      );

      if (!isInputField) {
        this.close();
      }
    }
  }

  close() {
    this.element.classList.add("opacity-0");
    this.panelTarget.classList.remove("translate-x-0");
    this.panelTarget.classList.add("translate-x-full");
    // The frame belongs to the page, not to this element.
    // `SlideoutComponent` renders inside it, but `close` also
    // runs from the `keydown` listener `disconnect` fails to
    // remove, and by then the overlay has left the document
    // and `closest` finds nothing. Everything else here is
    // about our own element and still has to happen, so this
    // guards the one line rather than the whole action.
    const slideoutFrame = this.element.closest("#slideout");
    if (slideoutFrame) slideoutFrame.removeAttribute("src");

    setTimeout(() => {
      this.element.remove();
    }, 300);
  }

  closeIfOutside(event) {
    if (this.preventCloseValue) return;

    // Check if the click originated from within the panel
    const clickPath = event.composedPath();
    const clickedInsidePanel = clickPath.includes(this.panelTarget);

    if (!clickedInsidePanel) {
      this.close();
    }
  }
}
